import React, { useState } from 'react';
import {
  Building2, Wrench, Truck, Settings, Award, Clock, Target,
  Shield, CheckCircle, Phone, Mail, MapPin, Menu, X,
  ArrowRight, ChevronDown } from
'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  heroData,
  aboutData,
  servicesData,
  featuresData,
  portfolioData,
  contactData } from
'../data/mockData';

const iconMap = {
  Building2, Wrench, Truck, Settings, Award, Clock, Target, Shield, CheckCircle, Phone
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Thank you! We\'ll get back to you within 24 hours.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">SteelCraft Welders</h1>
                <p className="text-xs text-slate-400">Precision Engineering</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-orange-500 transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('portfolio')} className="text-slate-300 hover:text-orange-500 transition-colors font-medium">
                Portfolio
              </button>
              <button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-orange-500 transition-colors font-medium">
                About
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-orange-500 hover:bg-orange-600 text-white">
                Get a Quote
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white">

              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen &&
          <nav className="md:hidden pb-4 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-slate-300 hover:text-orange-500 transition-colors py-2">
                Services
              </button>
              <button onClick={() => scrollToSection('portfolio')} className="block w-full text-left text-slate-300 hover:text-orange-500 transition-colors py-2">
                Portfolio
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-slate-300 hover:text-orange-500 transition-colors py-2">
                About
              </button>
              <Button
              onClick={() => scrollToSection('contact')}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                Get a Quote
              </Button>
            </nav>
          }
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${heroData.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>

          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {heroData.headline}
            </h2>
            <p className="text-xl md:text-2xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
              {heroData.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6 group">

                {heroData.primaryCTA}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('services')}
                size="lg"
                variant="outline"
                className="border-2 border-slate-400 text-white hover:bg-slate-800 hover:border-orange-500 text-lg px-8 py-6">

                {heroData.secondaryCTA}
              </Button>
            </div>
          </div>
        </div>

        <button
          onClick={() => scrollToSection('about')}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white animate-bounce">

          <ChevronDown className="w-8 h-8" />
        </button>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {aboutData.title}
            </h3>
            <p className="text-lg text-slate-600 leading-relaxed">
              {aboutData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Services
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive welding solutions for every industrial need
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {servicesData.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="service-card group hover:shadow-2xl transition-all duration-300 border-slate-200 overflow-hidden">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl text-slate-900">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose SteelCraft
            </h3>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Excellence in every weld, commitment in every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuresData.map((feature) => {
              const Icon = iconMap[feature.icon];
              return (
                <div
                  key={feature.id}
                  className="feature-item bg-slate-800/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700 hover:border-orange-500 transition-all duration-300 hover:transform hover:-translate-y-2">

                  <div className="w-14 h-14 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-orange-500" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">{feature.title}</h4>
                  <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="md:text-5xl !font-bold !text-4xl mb-4 text-slate-900">

            </h3>
            <p className="max-w-2xl !text-lg mx-auto text-slate-600">

            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioData.map((project) =>
            <div
              key={project.id}
              className="portfolio-item group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer aspect-square">

                <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <span className="inline-block px-3 py-1 bg-orange-500 rounded-full text-xs font-semibold mb-2">
                    {project.category}
                  </span>
                  <h4 className="text-xl font-bold">{project.title}</h4>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Get Your Free Quote
            </h3>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Ready to start your project? Contact us today for a free consultation and quote
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Send Us a Message</CardTitle>
                <CardDescription className="text-slate-400">
                  Fill out the form below and we'll respond within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      placeholder="Your full name" />

                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      placeholder="your.email@example.com" />

                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      placeholder="082 123 4567" />

                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Project Details *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      required
                      rows={5}
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      placeholder="Tell us about your welding project..." />

                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6">

                    Request Free Quote
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700">
                <h4 className="text-2xl font-bold text-white mb-6">Contact Information</h4>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Call Us</p>
                      <p className="text-lg font-semibold text-white">{contactData.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Email Us</p>
                      <p className="text-lg font-semibold text-white">{contactData.email}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Visit Us</p>
                      <p className="text-lg font-semibold text-white">{contactData.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-xl border border-slate-700">
                <h4 className="text-xl font-bold text-white mb-4">Business Hours</h4>
                <p className="text-slate-300 leading-relaxed">{contactData.hours}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12 border-t border-slate-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h5 className="text-lg font-bold text-white">SteelCraft Welders</h5>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your trusted partner for precision welding and industrial fabrication services.
              </p>
            </div>

            <div>
              <h5 className="text-lg font-bold text-white mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('portfolio')} className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Portfolio
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-bold text-white mb-4">Contact Details</h5>
              <div className="space-y-3">
                <p className="text-slate-400 text-sm">
                  <span className="text-orange-500 font-semibold">Phone:</span> {contactData.phone}
                </p>
                <p className="text-slate-400 text-sm">
                  <span className="text-orange-500 font-semibold">Email:</span> {contactData.email}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} SteelCraft Welders. All rights reserved. | Built for excellence.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky CTA Button */}
      <Button
        onClick={() => scrollToSection('contact')}
        className="fixed bottom-8 right-8 bg-orange-500 hover:bg-orange-600 text-white shadow-2xl z-40 px-6 py-6 text-base hidden md:flex">

        Get Free Quote
        <ArrowRight className="ml-2 w-5 h-5" />
      </Button>
    </div>);

};

export default LandingPage;